"use client";
import React from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  MotionValue,
} from "framer-motion";
import { Link } from "react-router-dom";
import { BackgroundRippleEffect } from "./background-ripple-effect";

export const HeroParallax = ({
  products,
  title,
  subtitle,
  description,
}: {
  products: {
    title: string;
    link: string;
    thumbnail: string;
  }[];
  title?: string;
  subtitle?: string;
  description?: string;
}) => {
  const firstRow = products.slice(0, 5);
  const secondRow = products.slice(5, 10);
  const thirdRow = products.slice(10, 15);
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const springConfig = { stiffness: 80, damping: 40, bounce: 0 };

  const translateX = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, 1000]),
    springConfig
  );
  const translateXReverse = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, -1000]),
    springConfig
  );
  const rotateX = useSpring(
    useTransform(scrollYProgress, [0, 0.2], [15, 0]),
    springConfig
  );
  const opacity = useSpring(
    useTransform(scrollYProgress, [0, 0.2], [0.2, 1]),
    springConfig
  );
  const rotateZ = useSpring(
    useTransform(scrollYProgress, [0, 0.2], [20, 0]),
    springConfig
  );
  const translateY = useSpring(
    useTransform(scrollYProgress, [0, 0.2], [-700, 500]),
    springConfig
  );

  return (
    <div
      ref={ref}
      className="h-[300vh] py-40 -mt-[15%] overflow-hidden antialiased relative flex flex-col self-auto [perspective:1000px] [transform-style:preserve-3d]"
    >
      {/* Background Ripple Effect - 100% width and height */}
      <div className="absolute inset-0 z-0 w-full h-full overflow-hidden pointer-events-auto">
        <BackgroundRippleEffect rows={20} cols={50} cellSize={60} />
      </div>

      {/* Header com z-index alto para ficar acima do ripple e dos cards */}
      <div className="relative z-20 flex items-center min-h-[70vh]">
        <Header title={title} subtitle={subtitle} description={description} />
      </div>
      <motion.div
        style={{
          rotateX,
          rotateZ,
          translateY,
          opacity,
        }}
        className="relative z-10"
      >
        <motion.div className="flex flex-row-reverse space-x-reverse space-x-12 mb-20">
          {firstRow.map((product, index) => (
            <ProductCard
              product={product}
              translate={translateX}
              key={`row1-${index}-${product.title}`}
            />
          ))}
        </motion.div>
        <motion.div className="flex flex-row mb-20 space-x-12">
          {secondRow.map((product, index) => (
            <ProductCard
              product={product}
              translate={translateXReverse}
              key={`row2-${index}-${product.title}`}
            />
          ))}
        </motion.div>
        <motion.div className="flex flex-row-reverse space-x-reverse space-x-12">
          {thirdRow.map((product, index) => (
            <ProductCard
              product={product}
              translate={translateX}
              key={`row3-${index}-${product.title}`}
            />
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Header = ({
  title = "Cássio",
  subtitle = "Diretor de Arte & Designer Gráfico",
  description = "Especialista em IA, criação de personagens digitais, desenvolvimento fullstack e design 3D. Transformo ideias em experiências visuais impactantes.",
}: {
  title?: string;
  subtitle?: string;
  description?: string;
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.25,
        delayChildren: 0.5,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, filter: "blur(10px)" },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: {
        duration: 1.2,
        ease: "easeOut" as const,
      },
    },
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <motion.div
      className="max-w-7xl relative mx-auto hero-padding container-padding w-full left-0 top-0 mt-[10%]"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.h1
        className="heading-display text-foreground"
        variants={itemVariants}
      >
        {title}
      </motion.h1>
      <motion.h2
        className="heading-section mt-2 gradient-text-primary"
        variants={itemVariants}
      >
        {subtitle}
      </motion.h2>
      <motion.p
        className="max-w-2xl text-body-lg mt-8 text-muted-foreground"
        variants={itemVariants}
      >
        {description}
      </motion.p>
      <motion.div
        className="flex gap-4 mt-8"
        variants={itemVariants}
      >
        <motion.div variants={buttonVariants}>
          <Link
            to="/projetos"
            className="inline-block px-6 py-3 rounded-lg liquid-glass-btn-solid text-white font-medium focus-ring"
          >
            Ver Projetos
          </Link>
        </motion.div>
        <motion.div variants={buttonVariants}>
          <Link
            to="/contato"
            className="inline-block px-6 py-3 rounded-lg liquid-glass-btn text-white font-medium focus-ring"
          >
            Falar Comigo
          </Link>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export const ProductCard = ({
  product,
  translate,
}: {
  product: {
    title: string;
    link: string;
    thumbnail: string;
  };
  translate: MotionValue<number>;
}) => {
  return (
    <motion.div
      style={{
        x: translate,
      }}
      whileHover={{
        y: -20,
      }}
      className="group/product h-96 w-[30rem] relative shrink-0"
    >
      <Link
        to={product.link}
        className="block group-hover/product:shadow-2xl"
      >
        <img
          src={product.thumbnail}
          height="600"
          width="600"
          className="object-cover object-left-top absolute h-full w-full inset-0 rounded-xl"
          alt={product.title}
        />
      </Link>
      <div className="absolute inset-0 h-full w-full opacity-0 group-hover/product:opacity-80 bg-black/80 pointer-events-none rounded-xl transition-opacity"></div>
      <h2 className="absolute bottom-4 left-4 opacity-0 group-hover/product:opacity-100 text-white font-medium text-lg transition-opacity">
        {product.title}
      </h2>
    </motion.div>
  );
};
